import { initUI, updateCardStatus, updateCardBody, row, enableUploadButton, setReportInfo } from "./ui";
import { detectNetworkType, NetworkTypeResult } from "./diagnostics/networkType";
import { detectIp, IpDetectionResult } from "./diagnostics/ipDetection";
import { checkDns, DnsCheckResult } from "./diagnostics/dnsCheck";
import { measureLatency, LatencyResult } from "./diagnostics/latency";
import { detectVpn, VpnDetectionResult } from "./diagnostics/vpnDetection";
import { uploadReport, setBackendOrder, DiagnosticReport } from "./report";

const DNS_DOMAINS = [
  "nettool-va-ali.rivergame.net",
  "nettool-va-gcp.topwargame.com",
  "nettool-va-cf.topwarapp.com",
  "nettool-va-aws.topwargame.com",
];

interface AllResults {
  networkType: NetworkTypeResult | null;
  ipDetection: IpDetectionResult | null;
  dnsCheck: DnsCheckResult | null;
  latency: LatencyResult | null;
  vpnDetection: VpnDetectionResult | null;
}

const results: AllResults = {
  networkType: null,
  ipDetection: null,
  dnsCheck: null,
  latency: null,
  vpnDetection: null,
};

async function runDiagnostics() {
  const startTime = Date.now();

  // Step 1: Network Type (instant, no async)
  updateCardStatus("network-type", "running");
  try {
    results.networkType = detectNetworkType();
    const nt = results.networkType;
    updateCardBody(
      "network-type",
      row("Connection", nt.type) +
      row("Effective Type", nt.effectiveType || "N/A") +
      row("Downlink", nt.downlink != null ? `${nt.downlink} Mbps` : "N/A") +
      row("RTT", nt.rtt != null ? `${nt.rtt} ms` : "N/A") +
      row("Save Data", nt.saveData ? "Yes" : "No")
    );
    updateCardStatus("network-type", "success");
  } catch {
    updateCardStatus("network-type", "error");
    updateCardBody("network-type", "Failed to detect network type");
  }

  // Step 2: IP Detection (async, calls multiple services)
  updateCardStatus("ip-detection", "running");
  updateCardBody("ip-detection", "Detecting public IP...");
  try {
    results.ipDetection = await detectIp();
    const ip = results.ipDetection;
    const hasIpv6 = ip.ipifyV6 && ip.ipifyV6.includes(":");
    updateCardBody(
      "ip-detection",
      row("Exit IP (ipify v4)", ip.ipifyV4 || "N/A") +
      row("Exit IP (ipify v6)", ip.ipifyV6 || "N/A") +
      row("Exit IP (ip.sb)", ip.fallbackIp || "N/A") +
      row("IPv6 Support", hasIpv6 ? "Yes" : "No") +
      row("WebRTC Local IPs", ip.webrtcLocalIps.length > 0 ? ip.webrtcLocalIps.join(", ") : "N/A")
    );
    updateCardStatus("ip-detection", "success");
  } catch {
    updateCardStatus("ip-detection", "error");
    updateCardBody("ip-detection", "Failed to detect IP");
  }

  // Step 3: DNS Check (all backend domains)
  updateCardStatus("dns-check", "running");
  updateCardBody("dns-check", `Resolving ${DNS_DOMAINS.length} domains...`);
  try {
    const dnsResults = await Promise.all(DNS_DOMAINS.map((d) => checkDns(d)));
    results.dnsCheck = dnsResults[0];
    let html = "";
    let allConsistent = true;
    for (const dns of dnsResults) {
      if (!dns.dnsConsistent) allConsistent = false;
      html += `<div style="margin-bottom:8px;padding-bottom:8px;border-bottom:1px solid rgba(255,255,255,0.1)">`;
      html += row("Domain", dns.domain);
      html += row("Alibaba DoH (A)", dns.aliDoh?.ipv4.join(", ") || "N/A");
      html += row("Alibaba DoH (AAAA)", dns.aliDoh?.ipv6.join(", ") || "N/A");
      html += row("Google DoH (A)", dns.googleDoh?.ipv4.join(", ") || "N/A");
      html += row("Google DoH (AAAA)", dns.googleDoh?.ipv6.join(", ") || "N/A");
      html += row("Consistent", dns.dnsConsistent ? "Yes" : "No - Results differ!");
      html += `</div>`;
    }
    updateCardBody("dns-check", html);
    updateCardStatus("dns-check", allConsistent ? "success" : "warning");
  } catch {
    updateCardStatus("dns-check", "error");
    updateCardBody("dns-check", "Failed to check DNS");
  }

  // Step 4: Backend Connectivity & Latency
  updateCardStatus("backend-connectivity", "running");
  updateCardBody("backend-connectivity", "Testing backend servers (0/4)...");
  try {
    results.latency = await measureLatency((completed, total) => {
      updateCardBody("backend-connectivity", `Testing backend servers (${completed}/${total})...`);
    });
    let html = "";
    let worstAvg = 0;
    let anyFailed = false;
    for (const ep of results.latency.endpoints) {
      if (ep.wsAvg > worstAvg) worstAvg = ep.wsAvg;
      const wsOk = ep.wsLatencies.length > 0;
      const httpOk = ep.httpLatency != null;
      if (!wsOk) anyFailed = true;

      const wsStatus = wsOk
        ? `<span style="color:#00ba7c">${ep.wsAvg} ms</span>`
        : `<span style="color:#f4212e">Failed</span>`;
      const httpStatus = httpOk
        ? `<span style="color:#00ba7c">${ep.httpLatency} ms</span>`
        : `<span style="color:#f4212e">Failed</span>`;

      html += `<div style="margin-bottom:8px;padding-bottom:8px;border-bottom:1px solid rgba(255,255,255,0.1)">`;
      html += row("Server", ep.name);
      html += row("Exit IP", ep.clientIp || "N/A");
      html += row("GeoIP", ep.geo || "N/A");
      html += row("HTTP", httpStatus);
      html += row("WebSocket", wsStatus);
      if (wsOk) {
        html += row("WS Min / Max", `${ep.wsMin} / ${ep.wsMax} ms`);
        html += row("WS Jitter", `${ep.wsJitter} ms`);
      }
      const t = ep.connectionTiming;
      if (t && t.dns === 0 && t.tcp === 0 && t.tls === 0) {
        html += row("Connection", "Reused");
      } else {
        html += row("DNS Lookup", t?.dns != null ? `${t.dns} ms` : "N/A");
        html += row("TCP Connect", t?.tcp != null ? `${t.tcp} ms` : "N/A");
        html += row("TLS Handshake", t?.tls != null ? `${t.tls} ms` : "N/A");
      }
      html += `</div>`;
    }
    updateCardBody("backend-connectivity", html);
    updateCardStatus("backend-connectivity", anyFailed ? "warning" : (worstAvg < 200 ? "success" : "warning"));
  } catch {
    updateCardStatus("backend-connectivity", "error");
    updateCardBody("backend-connectivity", "Failed to test backend connectivity");
  }

  // Step 5: VPN Detection (uses IP results)
  updateCardStatus("vpn-detection", "running");
  updateCardBody("vpn-detection", "Analyzing VPN indicators...");
  try {
    if (results.ipDetection) {
      results.vpnDetection = detectVpn(results.ipDetection);
      const vpn = results.vpnDetection;
      const likelihoodColor: Record<string, string> = {
        none: "#00ba7c",
        low: "#a0a4a8",
        medium: "#f7b928",
        high: "#f4212e",
      };
      updateCardBody(
        "vpn-detection",
        row("VPN Likelihood", `<span style="color:${likelihoodColor[vpn.vpnLikelihood]}">${vpn.vpnLikelihood.toUpperCase()}</span>`) +
        row("WebRTC Mismatch", vpn.signals.webrtcMismatch ? "Yes" : "No") +
        row("Timezone Mismatch", vpn.signals.timezoneMismatch ? "Yes" : "No") +
        row("Datacenter IP", vpn.signals.datacenterIp ? "Yes" : "No") +
        row("Multiple Public IPs", vpn.signals.multiplePublicIps ? "Yes" : "No") +
        row("Client Timezone", vpn.details.clientTimezone) +
        row("GeoIP Country", vpn.details.geoCountry || "N/A")
      );
      const status = vpn.vpnLikelihood === "none" ? "success" : vpn.vpnLikelihood === "high" ? "error" : "warning";
      updateCardStatus("vpn-detection", status);
    } else {
      updateCardBody("vpn-detection", "Skipped (IP detection failed)");
      updateCardStatus("vpn-detection", "warning");
    }
  } catch {
    updateCardStatus("vpn-detection", "error");
    updateCardBody("vpn-detection", "Failed to detect VPN");
  }

  const duration = Date.now() - startTime;
  document.querySelector(".subtitle")!.textContent = `Diagnostics completed in ${(duration / 1000).toFixed(1)}s`;

  // Sort backends by latency (lowest wsAvg first) for presign requests
  if (results.latency) {
    const sorted = [...results.latency.endpoints]
      .filter((ep) => ep.wsAvg > 0)
      .sort((a, b) => a.wsAvg - b.wsAvg)
      .map((ep) => `https://${ep.domain}`);
    if (sorted.length > 0) {
      setBackendOrder(sorted);
    }
  }

  // Enable upload
  enableUploadButton(async () => {
    const report: DiagnosticReport = {
      version: "1.0",
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      screenResolution: `${screen.width}x${screen.height}`,
      language: navigator.language,
      diagnostics: { ...results },
      duration,
    };

    setReportInfo("", "uploading");
    const uploadResult = await uploadReport(report);
    if (uploadResult.success) {
      setReportInfo(uploadResult.reportId ?? "", "success");
    } else {
      setReportInfo(uploadResult.error || "Unknown error", "error");
    }
  });
}

// Initialize
initUI();
runDiagnostics();
